import { useLayoutEffect, useRef, type ChangeEvent, type InputHTMLAttributes } from "react";
import { groupDigits, toRawNumber } from "@/utils/numberInput";

/** Position dans `display` juste après le n-ième chiffre (ou séparateur décimal / signe). */
function caretAfter(display: string, significant: number): number {
  if (significant <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < display.length; i += 1) {
    if (/[\d,-]/.test(display[i])) seen += 1;
    if (seen === significant) return i + 1;
  }
  return display.length;
}

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "type">;

/**
 * Champ de nombre qui sépare les milliers pendant la saisie (« 20 000 ») : l'utilisateur lit son montant
 * d'un coup d'œil, et `onChange` reçoit toujours la valeur brute (`e.target.value === "20000"`).
 * Les décimales sont acceptées quand `step` n'est pas entier (ex. « 0.5 » pour des heures).
 */
export default function NumberInput({ value, onChange, step, min, ...rest }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  const pendingCaret = useRef<number | null>(null);
  const allowDecimal = step === "any" || String(step ?? "").includes(".");
  const allowNegative = min !== undefined && Number(min) < 0;
  const display = groupDigits(value === undefined || value === null ? "" : String(value));

  useLayoutEffect(() => {
    if (pendingCaret.current === null || !ref.current || document.activeElement !== ref.current) return;
    const position = caretAfter(ref.current.value, pendingCaret.current);
    ref.current.setSelectionRange(position, position);
    pendingCaret.current = null;
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    const caret = el.selectionStart ?? el.value.length;
    pendingCaret.current = el.value.slice(0, caret).replace(/[^\d,.-]/g, "").length;
    // La page reçoit la valeur brute ; React réaffiche ensuite la valeur groupée.
    el.value = toRawNumber(el.value, allowDecimal, allowNegative);
    onChange?.(e);
  };

  return (
    <input
      {...rest}
      ref={ref}
      type="text"
      inputMode={allowDecimal ? "decimal" : "numeric"}
      autoComplete="off"
      value={display}
      onChange={handleChange}
    />
  );
}
